import { Toast } from "@base-ui/react/toast"
import * as jsQrModule from "jsqr"
import { useCallback, useEffect, useRef, useState } from "react"

const SCAN_RETRY_AFTER_ERROR_MS = 1200
const JSQR_SCAN_INTERVAL_MS = 150
const QR_SCANNER_CAMERA_CONSTRAINTS = {
  video: { facingMode: { ideal: "environment" } },
  audio: false,
} satisfies MediaStreamConstraints

type QrScannerCameraPermissionResult =
  | { granted: true }
  | { granted: false; reason: "denied" | "unavailable" }

type BarcodeDetectorCtor = {
  new (options?: {
    formats?: string[]
  }): {
    detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue?: string }>>
  }
  getSupportedFormats?: () => Promise<string[]>
}

type JsQrDecodeFn = (
  data: Uint8ClampedArray,
  width: number,
  height: number
) => { data: string } | null

function resolveJsQrDecoder(module: unknown): JsQrDecodeFn | null {
  const asFn = (value: unknown) =>
    typeof value === "function" ? (value as JsQrDecodeFn) : null

  if (typeof module === "function") return asFn(module)

  if (module && typeof module === "object") {
    const obj = module as Record<string, unknown>
    return asFn(obj.default) ?? asFn(obj.jsQR) ?? null
  }

  return null
}

type UseQrScannerOptions = {
  /** Whether the scanner should be active. */
  enabled: boolean
  /** Called with the raw QR code string when detected. Return true to accept (closes scanner), false to reject (shows error + retries). */
  onScan: (rawValue: string) => Promise<{ accepted: boolean; error?: string }>
}

type UseQrScannerResult = {
  videoRef: React.RefObject<HTMLVideoElement | null>
  error: string | null
  clearError: () => void
}

function getMediaErrorName(error: unknown): string {
  return typeof error === "object" &&
    error &&
    "name" in error &&
    typeof error.name === "string"
    ? error.name
    : ""
}

async function requestQrScannerCameraPermission(): Promise<QrScannerCameraPermissionResult> {
  if (!navigator.mediaDevices?.getUserMedia) {
    return { granted: false, reason: "unavailable" }
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia(
      QR_SCANNER_CAMERA_CONSTRAINTS
    )
    for (const track of stream.getTracks()) track.stop()
    return { granted: true }
  } catch (error) {
    return {
      granted: false,
      reason:
        getMediaErrorName(error) === "NotAllowedError"
          ? "denied"
          : "unavailable",
    }
  }
}

// Preflight camera access before opening a scanner dialog so the OS prompt
// does not stack on top of scanner UI that may never receive a camera stream.
export function useQrScannerPermissionGate(): () => Promise<boolean> {
  const { add } = Toast.useToastManager()

  return useCallback(async () => {
    const result = await requestQrScannerCameraPermission()
    if (result.granted) return true

    add({
      type: "error",
      title:
        result.reason === "denied"
          ? "Camera access denied"
          : "Camera unavailable",
      description:
        result.reason === "denied"
          ? "Allow camera access to scan QR codes."
          : "Camera access is not available on this device or browser.",
      timeout: 5000,
    })
    return false
  }, [add])
}

export function useQrScanner({
  enabled,
  onScan,
}: UseQrScannerOptions): UseQrScannerResult {
  const [error, setError] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const animationRef = useRef<number | null>(null)
  const cooldownRef = useRef<number | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const isProcessingRef = useRef(false)
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan

  const stopScanner = useCallback(() => {
    if (animationRef.current != null) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
    if (cooldownRef.current != null) {
      window.clearTimeout(cooldownRef.current)
      cooldownRef.current = null
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop()
      }
      streamRef.current = null
    }
    canvasRef.current = null
  }, [])

  const clearError = useCallback(() => setError(null), [])

  useEffect(() => {
    if (!enabled) return

    isProcessingRef.current = false
    let cancelled = false
    let detector: InstanceType<BarcodeDetectorCtor> | null = null

    const start = async () => {
      const BarcodeDetectorClass = (
        window as Window & { BarcodeDetector?: BarcodeDetectorCtor }
      ).BarcodeDetector

      if (!navigator.mediaDevices?.getUserMedia) {
        setError("Camera access is not available on this browser.")
        return
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia(
          QR_SCANNER_CAMERA_CONSTRAINTS
        )

        if (cancelled) {
          for (const track of stream.getTracks()) track.stop()
          return
        }

        let useBarcodeDetector = false
        if (BarcodeDetectorClass) {
          try {
            const formats = await BarcodeDetectorClass.getSupportedFormats?.()
            useBarcodeDetector = formats == null || formats.includes("qr_code")
          } catch {
            useBarcodeDetector = false
          }
        }

        if (useBarcodeDetector && BarcodeDetectorClass) {
          detector = new BarcodeDetectorClass({ formats: ["qr_code"] })
        }

        const decodeWithJsQr = resolveJsQrDecoder(jsQrModule)

        setError(null)
        streamRef.current = stream
        const video = videoRef.current
        if (!video) return
        video.srcObject = stream
        await video.play()

        const scan = async () => {
          if (cancelled) return

          const target = videoRef.current
          if (
            target &&
            target.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA &&
            !isProcessingRef.current
          ) {
            try {
              let firstRaw: string | null = null

              if (detector) {
                const barcodes = await detector.detect(target)
                firstRaw = barcodes[0]?.rawValue?.trim() ?? null
              } else if (decodeWithJsQr) {
                const width = target.videoWidth
                const height = target.videoHeight

                if (width > 0 && height > 0) {
                  let canvas = canvasRef.current
                  if (!canvas) {
                    canvas = document.createElement("canvas")
                    canvasRef.current = canvas
                  }
                  canvas.width = width
                  canvas.height = height

                  const context = canvas.getContext("2d", {
                    willReadFrequently: true,
                  })
                  if (context) {
                    context.drawImage(target, 0, 0, width, height)
                    const imageData = context.getImageData(0, 0, width, height)
                    const decoded = decodeWithJsQr(
                      imageData.data,
                      width,
                      height
                    )
                    firstRaw = decoded?.data?.trim() ?? null
                  }
                }
              }

              if (firstRaw) {
                isProcessingRef.current = true
                const result = await onScanRef.current(firstRaw)

                if (result.accepted) {
                  return // Stop scanning — caller closes the scanner
                }

                setError(
                  result.error ?? "Unrecognized QR code. Please try again."
                )
                cooldownRef.current = window.setTimeout(() => {
                  isProcessingRef.current = false
                }, SCAN_RETRY_AFTER_ERROR_MS)
              }
            } catch {
              setError(
                "Unable to read this QR code. Try again or enter address manually."
              )
              cooldownRef.current = window.setTimeout(() => {
                isProcessingRef.current = false
              }, SCAN_RETRY_AFTER_ERROR_MS)
            }
          }

          // BarcodeDetector.detect() is async and self-throttles.
          // jsQR is synchronous — throttle to ~7fps to avoid burning CPU on iOS 120Hz.
          if (detector) {
            animationRef.current = requestAnimationFrame(scan)
          } else {
            cooldownRef.current = window.setTimeout(() => {
              animationRef.current = requestAnimationFrame(scan)
            }, JSQR_SCAN_INTERVAL_MS)
          }
        }

        animationRef.current = requestAnimationFrame(scan)
      } catch (error) {
        const errorName = getMediaErrorName(error)

        if (
          errorName === "NotAllowedError" ||
          errorName === "NotFoundError" ||
          errorName === "NotReadableError"
        ) {
          setError(
            "Camera access was denied or is unavailable. Enable camera access to scan a QR code."
          )
          return
        }

        setError("Unable to initialize QR scanner. Please try again.")
      }
    }

    start()

    return () => {
      cancelled = true
      stopScanner()
    }
  }, [enabled, stopScanner])

  return { videoRef, error, clearError }
}
